/**
 * 정렬 JSON(`ccatlas-cjson/1`)과 SHA-256 (TASK-2026-003 CR-01 §3.2).
 *
 * 결과 JSON의 digest, batch의 내용 hash, 저장 확인 기록의 `content_hash`가
 * 모두 이 두 함수에서 나온다. 브라우저와 서버가 **같은 바이트**를 만들어야
 * "같은 내용"을 다툴 수 있으므로 DOM·Node API 없이 순수하게 둔다.
 *
 * - 객체 키는 UTF-16 코드 단위 순으로 정렬한다(`Array.prototype.sort` 기본).
 * - 공백이 없고 문자열·수는 `JSON.stringify` 규칙을 따른다.
 * - `undefined`·함수·`NaN`·`Infinity`·순환 참조·일반 객체가 아닌 값은
 *   **던진다.** `JSON.stringify`처럼 조용히 빼면 서로 다른 입력이 같은 hash가 된다.
 */

export const CANONICALIZATION = 'ccatlas-cjson/1';

function isPlainObject(value: object): boolean {
  const proto = Object.getPrototypeOf(value) as unknown;
  return proto === Object.prototype || proto === null;
}

function encode(value: unknown, seen: Set<object>, path: string): string {
  if (value === null) {
    return 'null';
  }
  switch (typeof value) {
    case 'boolean':
      return value ? 'true' : 'false';
    case 'number':
      if (!Number.isFinite(value)) {
        throw new TypeError(`${path}: 유한한 수가 아닙니다.`);
      }
      return JSON.stringify(value);
    case 'string':
      return JSON.stringify(value);
    case 'object':
      break;
    default:
      throw new TypeError(`${path}: 담을 수 없는 값(${typeof value})입니다.`);
  }

  const object = value as object;
  if (seen.has(object)) {
    throw new TypeError(`${path}: 순환 참조입니다.`);
  }
  seen.add(object);
  try {
    if (Array.isArray(object)) {
      return `[${object.map((item, index) => encode(item, seen, `${path}[${String(index)}]`)).join(',')}]`;
    }
    if (!isPlainObject(object)) {
      throw new TypeError(`${path}: 일반 객체가 아닙니다.`);
    }
    const record = object as Record<string, unknown>;
    const parts = Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${encode(record[key], seen, `${path}.${key}`)}`);
    return `{${parts.join(',')}}`;
  } finally {
    seen.delete(object);
  }
}

/** 값을 `ccatlas-cjson/1` 문자열로 만든다. 담을 수 없는 값이면 던진다. */
export function canonicalize(value: unknown): string {
  return encode(value, new Set<object>(), '$');
}

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

/** UTF-8 문자열의 SHA-256을 소문자 16진수 64자로 돌려준다(FIPS 180-4). */
export function sha256Hex(text: string): string {
  const message = new TextEncoder().encode(text);
  const bitLength = message.length * 8;
  const blockCount = Math.ceil((message.length + 9) / 64);
  const padded = new Uint8Array(blockCount * 64);
  padded.set(message);
  padded[message.length] = 0x80;
  const view = new DataView(padded.buffer);
  // 길이는 64비트 big-endian. 2^53 미만이므로 상위·하위 32비트로 나눠 적는다.
  view.setUint32(padded.length - 8, Math.floor(bitLength / 0x1_0000_0000));
  view.setUint32(padded.length - 4, bitLength >>> 0);

  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const w = new Uint32Array(64);

  for (let block = 0; block < blockCount; block += 1) {
    const offset = block * 64;
    for (let i = 0; i < 16; i += 1) {
      w[i] = view.getUint32(offset + i * 4);
    }
    for (let i = 16; i < 64; i += 1) {
      const w15 = w[i - 15] ?? 0;
      const w2 = w[i - 2] ?? 0;
      const s1 = ((w15 >>> 7) | (w15 << 25)) ^ ((w15 >>> 18) | (w15 << 14)) ^ (w15 >>> 3);
      const s2 = ((w2 >>> 17) | (w2 << 15)) ^ ((w2 >>> 19) | (w2 << 13)) ^ (w2 >>> 10);
      w[i] = ((w[i - 16] ?? 0) + s1 + (w[i - 7] ?? 0) + s2) >>> 0;
    }

    let a = h[0] ?? 0;
    let b = h[1] ?? 0;
    let c = h[2] ?? 0;
    let d = h[3] ?? 0;
    let e = h[4] ?? 0;
    let f = h[5] ?? 0;
    let g = h[6] ?? 0;
    let hh = h[7] ?? 0;

    for (let i = 0; i < 64; i += 1) {
      const s1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + s1 + ch + (K[i] ?? 0) + (w[i] ?? 0)) >>> 0;
      const s0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (s0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }

    h[0] = ((h[0] ?? 0) + a) >>> 0;
    h[1] = ((h[1] ?? 0) + b) >>> 0;
    h[2] = ((h[2] ?? 0) + c) >>> 0;
    h[3] = ((h[3] ?? 0) + d) >>> 0;
    h[4] = ((h[4] ?? 0) + e) >>> 0;
    h[5] = ((h[5] ?? 0) + f) >>> 0;
    h[6] = ((h[6] ?? 0) + g) >>> 0;
    h[7] = ((h[7] ?? 0) + hh) >>> 0;
  }

  return Array.from(h, (word) => word.toString(16).padStart(8, '0')).join('');
}

/** `sha256:<hex64>` 형식의 내용 hash. */
export function contentHash(value: unknown): string {
  return `sha256:${sha256Hex(canonicalize(value))}`;
}

/** 이미 만든 문자열의 hash. 같은 문자열을 두 번 정렬하지 않으려고 둔다. */
export function textHash(text: string): string {
  return `sha256:${sha256Hex(text)}`;
}

export const CONTENT_HASH_PATTERN = /^sha256:[0-9a-f]{64}$/;
